import type { DayReport, FlightHistoryRow, FlightSeriesDay, SecurityDay, SecurityHistory, ShowUpCurve } from "./api";

/**
 * The arithmetic behind the History page: fill percentages and the one scale that colours
 * them, the summary tiles, the flights-per-day chart and the per-day chart. Kept apart from
 * the components so each figure can be checked on its own, because a bar that is drawn
 * wrong looks exactly like a bar that is drawn right.
 */

/** Airline passengers against seats, as a percentage; null unless both are known. */
export function fillPct(pax: number | null, seats: number | null): number | null {
  if (pax == null || seats == null || seats <= 0) return null;
  return (pax / seats) * 100;
}

export type FillBand = "none" | "empty" | "light" | "good" | "full" | "over";

export interface FillTone {
  band: FillBand;
  /** The word for it, for a title or a legend. */
  label: string;
  /** The background class for a bar or a swatch. */
  bar: string;
  /** The text class for a figure standing on its own. */
  text: string;
}

const TONES: Record<FillBand, FillTone> = {
  none: { band: "none", label: "not measured", bar: "bg-ink-3/40", text: "text-ink-3" },
  empty: { band: "empty", label: "empty", bar: "bg-bad/70", text: "text-bad" },
  light: { band: "light", label: "light", bar: "bg-warn", text: "text-warn" },
  good: { band: "good", label: "good", bar: "bg-ok/80", text: "text-ok" },
  full: { band: "full", label: "full", bar: "bg-ok", text: "text-ok" },
  over: { band: "over", label: "over", bar: "bg-bad", text: "text-bad" },
};

/** The bands in the order a strip or a legend shows them. */
export const FILL_BANDS: FillBand[] = ["empty", "light", "good", "full", "over"];

/**
 * One scale for every fill shown anywhere, so a 62% in a table, a tile and a strip is the
 * same amber. Under 40 is empty, 40 to 69 light, 70 to 89 good, 90 to 100 full, and past
 * 100 is oversold, which is red for the opposite reason an empty flight is.
 */
export function fillTone(pct: number | null): FillTone {
  if (pct == null || Number.isNaN(pct)) return TONES.none;
  if (pct > 100) return TONES.over;
  if (pct >= 90) return TONES.full;
  if (pct >= 70) return TONES.good;
  if (pct >= 40) return TONES.light;
  return TONES.empty;
}

/** The tone for a band already known, for a legend or a strip segment. */
export function bandTone(band: FillBand): FillTone {
  return TONES[band];
}

/**
 * Each band's edges in words, for a legend, a chip or the How-to-read box. Written beside
 * fillTone so the words and the arithmetic cannot drift apart; the guide's scale table is
 * pinned to the same edges by fill-tone.test.ts.
 */
const RANGES: Record<FillBand, string> = { none: "", empty: "under 40%", light: "40–69%", good: "70–89%", full: "90–100%", over: "past 100%" };
export function bandRange(band: FillBand): string {
  return RANGES[band];
}

/** How many of a set of fills fall in each band; a null fill counts under "none". */
export function bandCounts(fills: Array<number | null>): Record<FillBand, number> {
  const counts: Record<FillBand, number> = { none: 0, empty: 0, light: 0, good: 0, full: 0, over: 0 };
  for (const f of fills) counts[fillTone(f).band]++;
  return counts;
}

/**
 * A fill as a bar: full width is 100%, and anything over it is shown at full width with
 * the real figure in the label, so an oversold flight reads as oversold rather than as a
 * drawing error.
 */
export function fillBar(fill: number | null): { width: number; label: string; over: boolean } | null {
  if (fill == null) return null;
  return { width: Math.max(0, Math.min(100, fill)), label: `${Math.round(fill)}%`, over: fill > 100 };
}

export interface HistorySummary {
  /** Rows on the table: flight numbers, counting each direction separately. */
  flights: number;
  operations: number;
  cancelled: number;
  /** Airline passengers over seats across the operations that reported both; null when none did. */
  avgFill: number | null;
  fillFrom: number;
  /** Passengers through the checkpoint, and how many departures that rests on. */
  s2: number | null;
  s2From: number;
}

/**
 * The tiles above the table. The average fill is passengers over seats across operations,
 * not an average of row percentages: a daily 737 and a weekly Dash 8 should weigh what
 * they carried, not count once each.
 */
export function summarize(rows: FlightHistoryRow[]): HistorySummary {
  let pax = 0;
  let seats = 0;
  let fillFrom = 0;
  let s2 = 0;
  let s2From = 0;
  for (const r of rows) {
    const p = rowPax(r);
    if (r.seats && p) {
      pax += p.total;
      seats += r.seats * p.from;
      fillFrom += p.from;
    }
    if (r.direction === "DEP" && r.s2.from > 0) {
      s2 += r.s2.total;
      s2From += r.s2.from;
    }
  }
  return {
    flights: rows.length,
    operations: rows.reduce((n, r) => n + r.operations, 0),
    cancelled: rows.reduce((n, r) => n + r.cancelled, 0),
    avgFill: seats > 0 ? (pax / seats) * 100 : null,
    fillFrom,
    s2: s2From > 0 ? s2 : null,
    s2From,
  };
}

/**
 * A row's fill over the range: its average passengers against its usual seats. The
 * airline's own figure when there is one; for a departure without it, the passengers
 * the checkpoint counted, which is a floor (a passenger who connected airside is not
 * counted) and is labelled as such wherever it is shown.
 */
export function rowFill(r: FlightHistoryRow): number | null {
  const p = rowPax(r);
  return p ? fillPct(p.total / p.from, r.seats) : null;
}

/** The passenger figure a row's fill rests on: the airline's, else the checkpoint's for a departure. */
export function rowPax(r: FlightHistoryRow): { total: number; from: number } | null {
  if (r.airlinePax.from > 0) return r.airlinePax;
  if (r.direction === "DEP" && r.s2.from > 0) return r.s2;
  return null;
}

/** Where a row's fill comes from, for its label. */
export function rowFillBasis(r: FlightHistoryRow): "airline" | "security" | null {
  if (r.airlinePax.from > 0) return r.seats ? "airline" : null;
  if (r.direction === "DEP" && r.s2.from > 0) return r.seats ? "security" : null;
  return null;
}

export interface AirlineFill {
  airline: string;
  iata: string | null;
  name: string;
  /** Passengers over seats across the airline's operations that reported both; null when none did. */
  fill: number | null;
  /** The operations that figure rests on. */
  from: number;
  /** Flight numbers on the table for this airline, each direction its own. */
  flights: number;
}

/**
 * The table's rows folded into one line per airline, fullest first. Weighted the way the
 * tiles are, by operations, so one airline's busy daily flight is not outvoted by its
 * weekly one. Airlines with no fill at all sit at the end: nothing measured is not last
 * place, but it is not a ranking either.
 */
export function fillByAirline(rows: FlightHistoryRow[]): AirlineFill[] {
  const by = new Map<string, AirlineFill & { pax: number; seats: number }>();
  for (const r of rows) {
    const a = by.get(r.airline) ?? { airline: r.airline, iata: r.iata, name: r.airlineName, fill: null, from: 0, flights: 0, pax: 0, seats: 0 };
    a.flights++;
    const p = rowPax(r);
    if (r.seats && p) {
      a.pax += p.total;
      a.seats += r.seats * p.from;
      a.from += p.from;
    }
    by.set(r.airline, a);
  }
  return [...by.values()]
    .map(({ pax, seats, ...a }) => ({ ...a, fill: seats > 0 ? (pax / seats) * 100 : null }))
    .sort((x, y) => {
      if (x.fill == null && y.fill == null) return x.name.localeCompare(y.name);
      if (x.fill == null) return 1;
      if (y.fill == null) return -1;
      return y.fill - x.fill || x.name.localeCompare(y.name);
    });
}

const n = (v: number) => v.toLocaleString("en-CA");
const plural = (v: number, one: string, many = `${one}s`) => `${n(v)} ${v === 1 ? one : many}`;

/** "a, b and c": a list said the way a sentence says it. */
function list(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * The Figures tab's opening line: the range's totals said in one sentence, so the page
 * can be read before any tile is. `recent` is whether the range ends today, which is the
 * difference between "in the last 30 days" and "over these 30 days". Nothing measured is
 * said as such: an on-time share of null is left out, never written as 0%.
 */
export function figuresSentence(totals: DayReport | null, days: number, where: { place: string; recent: boolean }): string {
  if (!totals || totals.flights === 0) return "No flights were kept for these dates.";
  const span = days === 1 ? "On this day" : where.recent ? `In the last ${days} days` : `Over these ${days} days`;
  const parts: string[] = [];
  if (totals.onTimePct != null) parts.push(`${totals.onTimePct}% left or arrived within 15 minutes of schedule`);
  if (totals.avgDelayMin != null) {
    const m = Math.abs(totals.avgDelayMin);
    parts.push(totals.avgDelayMin < 0 ? `flights ran ${plural(m, "minute")} early on average` : `the average delay was ${plural(m, "minute")}`);
  }
  parts.push(totals.cancelled === 0 ? "none were cancelled" : `${plural(totals.cancelled, "flight was", "flights were")} cancelled`);
  const first = parts[0]!;
  return `${span} ${where.place} handled ${plural(totals.flights, "flight")}. ${first[0]!.toUpperCase()}${first.slice(1)}${parts.length > 1 ? `, ${list(parts.slice(1))}` : ""}.`;
}

/**
 * The By flight tab's opening line: how many flight numbers are on the table, how full
 * they flew, and which airline was fullest and emptiest. The comparison is only made when
 * two or more airlines measured a fill; one airline cannot be both ends of a scale.
 */
export function flightsSentence(rows: FlightHistoryRow[], days: number): string {
  if (rows.length === 0) return "No flights match this filter in these dates.";
  const s = summarize(rows);
  const head = `Showing ${plural(s.flights, "flight number")} over ${plural(days, "day")}.`;
  if (s.avgFill == null) return `${head} No flight reported both passengers and seats, so how full they flew is not known.`;
  const measured = fillByAirline(rows).filter((a) => a.fill != null);
  const fill = `On average they flew ${Math.round(s.avgFill)}% full`;
  if (measured.length < 2) return `${head} ${fill}.`;
  const fullest = measured[0]!;
  const emptiest = measured[measured.length - 1]!;
  return `${head} ${fill}; ${fullest.name}'s flights were the fullest (${Math.round(fullest.fill!)}%) and ${emptiest.name}'s the emptiest (${Math.round(emptiest.fill!)}%).`;
}

export interface DayBar {
  date: string;
  /** Left edge and width in the chart's 0..100 space. */
  x: number;
  w: number;
  /** Top edge and height of each stacked part, in the same space; departures sit on the base, arrivals on them. */
  dep: { y: number; h: number };
  arr: { y: number; h: number };
  /** A thin red cap on the stack when any flight that day was cancelled; null when none was. */
  cancelled: { y: number; h: number } | null;
  /** Where the on-time line passes, on its own 0..100 scale; null when no delay was measured that day. */
  onTimeY: number | null;
  day: DayReport;
}

export interface DaysShape {
  bars: DayBar[];
  /** The flights at the top of the chart, so a height can be read back. */
  top: number;
  /** Y gridlines, as flights. */
  ticks: number[];
  /** The on-time line, joining only the days that measured one. */
  onTime: Array<{ x: number; y: number }>;
}

/** The least a cancelled cap is drawn at, so one cancellation on a busy day is still a mark. */
const CAP_MIN = 2.5;

/**
 * Days into stacked bars. The scale is the busiest day. The cancelled cap is drawn over the
 * top of the stack at its share of the day's flights, never thinner than a mark: the stack
 * already counts the cancelled flights, so the cap says how much of it never flew.
 */
export function shapeDays(days: DayReport[]): DaysShape {
  const top = Math.max(1, ...days.map((d) => d.flights));
  const step = top > 60 ? 20 : top > 30 ? 10 : top > 12 ? 5 : top > 5 ? 2 : 1;
  const ticks: number[] = [];
  for (let v = step; v <= top; v += step) ticks.push(v);
  const w = days.length ? 100 / days.length : 100;
  const h = (n: number) => (n / top) * 100;
  const bars = days.map((d, i) => {
    const dep = { y: 100 - h(d.departures), h: h(d.departures) };
    // The top of the stack is worked from the sum and the arrivals' height from the two
    // edges, so the parts tile exactly and the busiest day meets 0 rather than an ulp off it.
    const arrY = 100 - h(d.departures + d.arrivals);
    const arr = { y: arrY, h: dep.y - arrY };
    const cap = d.cancelled > 0 ? Math.max(CAP_MIN, h(d.cancelled)) : 0;
    return {
      date: d.date,
      x: i * w,
      w,
      dep,
      arr,
      cancelled: d.cancelled > 0 ? { y: arr.y, h: cap } : null,
      onTimeY: d.onTimePct == null ? null : 100 - d.onTimePct,
      day: d,
    };
  });
  return {
    bars,
    top,
    ticks,
    onTime: bars.filter((b) => b.onTimeY != null).map((b) => ({ x: b.x + b.w / 2, y: b.onTimeY! })),
  };
}

export interface SeriesBar {
  date: string;
  cancelled: boolean;
  /** Left edge and width in the chart's 0..100 space. */
  x: number;
  w: number;
  /** Heights in the chart's 0..100 space; null where nothing was measured that day. */
  pax: number | null;
  s2: number | null;
  day: FlightSeriesDay;
}

export interface SeriesShape {
  bars: SeriesBar[];
  /** The value at the top of the chart, so a height can be read back. */
  top: number;
  /** The seats line's height, or null when the type is unknown. */
  seatsY: number | null;
  /** Y gridlines, as values. */
  ticks: number[];
  /** Whether any day has a figure at all; a chart of nothing says so instead. */
  measured: boolean;
}

/**
 * Days into bars. The scale is set by the biggest figure or the seats, whichever is
 * larger, so an oversold day pokes above the line rather than clipping it. A measured
 * zero keeps a bar of zero height, which the chart draws as a hairline: a zero that was
 * counted is a fact, and a day with no count at all gets no bar.
 */
export function shapeSeries(days: FlightSeriesDay[], seats: number | null): SeriesShape {
  const values = days.flatMap((d) => [d.airlinePax, d.s2]).filter((v): v is number => v != null);
  const measured = values.length > 0;
  const top = Math.max(1, ...values, seats ?? 0);
  const step = top > 300 ? 100 : top > 120 ? 50 : top > 60 ? 25 : 10;
  const ticks: number[] = [];
  for (let v = step; v <= top; v += step) ticks.push(v);
  const w = days.length ? 100 / days.length : 100;
  const h = (v: number | null) => (v == null ? null : (v / top) * 100);
  return {
    bars: days.map((d, i) => ({ date: d.date, cancelled: d.cancelled, x: i * w, w, pax: h(d.airlinePax), s2: h(d.s2), day: d })),
    top,
    seatsY: seats != null && seats > 0 ? (seats / top) * 100 : null,
    ticks,
    measured,
  };
}

/* ------------------------------------------------------------------ security -- */

/** A queue wait said the way a duty manager does: "45 s", "6 min 20 s", "1 h 05 min". */
export function waitWords(sec: number | null | undefined): string {
  if (sec == null) return "—";
  if (sec < 60) return `${Math.round(sec)} s`;
  if (sec < 3600) {
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return s ? `${m} min ${s} s` : `${m} min`;
  }
  const h = Math.floor(sec / 3600);
  return `${h} h ${String(Math.round((sec % 3600) / 60)).padStart(2, "0")} min`;
}

/** The bands' labels: "0–5 min" ... "30+ min". */
export function bandLabels(edges: number[]): string[] {
  return edges.map((e, i) => (i < edges.length - 1 ? `${e}–${edges[i + 1]} min` : `${e}+ min`));
}

/** "85 min" up to two hours, "2 h 10 min" past it: how a duty manager says a lead time. */
export function leadWords(min: number | null): string {
  if (min == null) return "—";
  if (min < 120) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * The Security tab's opening line: passengers through the checkpoint over the range, the
 * busiest quarter hour, and how far ahead of their flight half the passengers cleared.
 * Each part is said only when it was measured. `when` turns the peak's instant into the
 * airport's words, which needs the zone, so the component supplies it.
 */
export function securitySentence(h: SecurityHistory | null, days: number, where: { place: string; recent: boolean }, when: (iso: string) => string): string {
  if (!h || h.totals.screened == null) return "Nothing was recorded at the security checkpoint for these dates.";
  const span = days === 1 ? "On this day" : where.recent ? `In the last ${days} days` : `Over these ${days} days`;
  const t = h.totals;
  let s = `${span} ${plural(t.screened!, "passenger")} went through security at ${where.place}`;
  s += t.daysMeasured < days ? `, counted on ${plural(t.daysMeasured, "day")} of ${n(days)}.` : ".";
  if (t.peak) s += ` The busiest quarter hour was ${when(t.peak.at)}, with ${plural(t.peak.scans, "passenger")} (${n(t.peak.perHour)} an hour).`;
  if (h.curves.all.half != null) s += ` Half of a flight's passengers had cleared ${leadWords(h.curves.all.half)} before it was due to leave.`;
  return s;
}

export interface HeatCell {
  hour: number;
  /** Passengers that hour, or null when the day was not read at all. */
  v: number | null;
  /** 0..1 against the busiest hour in the range, for the colour. */
  level: number;
}

export interface HeatShape {
  /** The clock hours shown, from the first that ever saw a passenger to the last. */
  hours: number[];
  rows: Array<{ date: string; total: number | null; cells: HeatCell[] }>;
  max: number;
  /** The average passengers per hour across the days measured, for the strip under the grid. */
  typical: Array<{ hour: number; avg: number; level: number }>;
}

/**
 * Days by clock hour, for a grid where the dark cells are the busy ones. The hours run
 * from the first that any day used to the last, so a checkpoint that opens at 04:00 does
 * not spend a quarter of the grid on the night. Every cell is scaled to the busiest hour
 * in the range, so one colour means the same number of passengers on every row.
 */
export function shapeHeat(days: SecurityDay[]): HeatShape {
  const measured = days.filter((d) => d.hours != null);
  const used = Array.from({ length: 24 }, (_, h) => measured.some((d) => (d.hours![h] ?? 0) > 0));
  const first = used.indexOf(true);
  const last = used.lastIndexOf(true);
  const hours = first < 0 ? [] : Array.from({ length: last - first + 1 }, (_, i) => first + i);
  const max = Math.max(1, ...measured.flatMap((d) => d.hours!));
  const rows = days.map((d) => ({
    date: d.date,
    total: d.screened,
    cells: hours.map((hour) => {
      const v = d.hours ? (d.hours[hour] ?? 0) : null;
      return { hour, v, level: v == null ? 0 : v / max };
    }),
  }));
  const avgs = hours.map((hour) => (measured.length ? measured.reduce((a, d) => a + (d.hours![hour] ?? 0), 0) / measured.length : 0));
  const topAvg = Math.max(1, ...avgs);
  return { hours, rows, max, typical: hours.map((hour, i) => ({ hour, avg: avgs[i]!, level: avgs[i]! / topAvg })) };
}

/**
 * A cell's colour: the info hue mixed into the panel, never a guessed hex, so it holds in
 * either theme. A measured zero is a faint wash; an unmeasured day is left to the panel.
 */
export function heatColor(cell: { v: number | null; level: number }): string | undefined {
  if (cell.v == null) return undefined;
  if (cell.v === 0) return "color-mix(in srgb, var(--color-info) 6%, transparent)";
  return `color-mix(in srgb, var(--color-info) ${Math.round(14 + cell.level * 86)}%, transparent)`;
}

export interface CurveShape {
  /** One bar per quarter hour, T-240 first: left edge, width and height in a 0..100 box. */
  bars: Array<{ x: number; w: number; h: number; share: number; minsBefore: number }>;
  /** The share cleared so far, as a line across the top scale: 0 at the left, 100 at departure. */
  cumulative: Array<{ x: number; y: number }>;
  /** The tallest bar's share, so a height can be read back. */
  top: number;
  /** Where half and four in five had cleared, along the same axis; null when not measured. */
  halfX: number | null;
  mostX: number | null;
}

/**
 * A show-up curve as quarter-hour bars with the running total over them. The bars are
 * scaled to the tallest so the shape reads; the running total has its own 0..100 scale,
 * because "most of them by T-60" is what a lane plan is written from.
 */
export function shapeCurve(curve: ShowUpCurve): CurveShape {
  const n = curve.share.length || 1;
  const w = 100 / n;
  const top = Math.max(1, ...curve.share);
  let cum = 0;
  const cumulative: Array<{ x: number; y: number }> = [{ x: 0, y: 100 }];
  const bars = curve.share.map((share, i) => {
    cum += share;
    cumulative.push({ x: (i + 1) * w, y: 100 - Math.min(100, cum) });
    return { x: i * w, w, h: (share / top) * 100, share, minsBefore: (n - i) * 15 };
  });
  const along = (min: number | null) => (min == null ? null : ((n * 15 - min) / (n * 15)) * 100);
  return { bars, cumulative, top, halfX: along(curve.half), mostX: along(curve.most) };
}
